import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { MapPin, Phone, Mail, Linkedin, Twitter, Facebook, Instagram } from 'lucide-react';

const API = '/api';

// Logo URLs - use white text version for dark backgrounds
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_8c6a1162-6fec-4154-8fe3-0b918a141fc6/artifacts/ojlspj1s_Untitled%20design.png';

export const Footer = () => {
  const { t, language, direction } = useLanguage();
  const [footerContent, setFooterContent] = useState(null);
  const [contactContent, setContactContent] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${API}/cms/page-content?sections=footer,contact_info`);
        if (response.ok) {
          const data = await response.json();
          setFooterContent(data.footer);
          setContactContent(data.contact_info);
        }
      } catch (error) {
        console.error('Error fetching footer content:', error);
      }
    };
    fetchContent();
  }, []);

  // Helper to get content with fallback
  const getFooterContent = (field, fallback) => {
    if (footerContent) {
      const content = language === 'ar' ? footerContent.content_ar : footerContent.content_en;
      if (content && content[field]) {
        return content[field];
      }
    }
    return fallback || '';
  };

  const getContactContent = (field, fallback) => {
    if (contactContent) {
      const content = language === 'ar' ? contactContent.content_ar : contactContent.content_en;
      if (content && content[field]) {
        return content[field];
      }
    }
    return fallback || '';
  };

  // Social links from CMS (only show if URL is provided)
  const socialLinks = [
    { 
      url: getFooterContent('social_linkedin', ''), 
      icon: Linkedin, 
      label: 'LinkedIn', 
      testId: 'social-linkedin' 
    },
    { 
      url: getFooterContent('social_twitter', ''), 
      icon: Twitter, 
      label: 'Twitter', 
      testId: 'social-twitter' 
    },
    { 
      url: getFooterContent('social_facebook', ''), 
      icon: Facebook, 
      label: 'Facebook', 
      testId: 'social-facebook' 
    },
    { 
      url: getFooterContent('social_instagram', ''), 
      icon: Instagram, 
      label: 'Instagram', 
      testId: 'social-instagram' 
    },
  ].filter(link => link.url && link.url.trim() !== '');

  const quickLinks = [
    { label: t('nav.home'), href: '/' },
    { label: t('nav.about'), href: '/about' },
    { label: t('nav.jobs'), href: '/jobs' },
    { label: t('nav.courses'), href: '/courses' },
    { label: t('nav.contact'), href: '/contact' },
  ];

  const serviceLinks = [
    { label: t('nav.recruitment'), href: '/services/recruitment' },
    { label: t('nav.training'), href: '/services/training' },
    { label: t('nav.consulting'), href: '/services/consulting' },
    { label: t('nav.careerAdvisory'), href: '/services/career-advisory' },
  ];

  const legalLinks = [
    { label: t('footer.privacy'), href: '/privacy' },
    { label: t('footer.terms'), href: '/terms' },
    { label: t('footer.cookies'), href: '/cookies' },
  ];

  return (
    <footer className="bg-[#111827] text-white" data-testid="main-footer">
      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <div className="bg-white rounded-lg p-2 inline-block">
                <img src={LOGO_URL} alt="WeDo HR Services" className="h-10 w-auto" />
              </div>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              {getFooterContent('description', t('footer.description'))}
            </p>
            {/* Social Links - Only show if configured */}
            {socialLinks.length > 0 && (
              <div className="flex items-center gap-4">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#33AADD] transition-colors"
                    aria-label={social.label}
                    data-testid={social.testId}
                  >
                    <social.icon className="h-5 w-5" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-[#33AADD] transition-colors text-sm"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6">{t('footer.services')}</h4>
            <ul className="space-y-3">
              {serviceLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-[#33AADD] transition-colors text-sm"
                    data-testid={`footer-service-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6">{t('nav.contact')}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#33AADD] mt-0.5 flex-shrink-0" />
                <span className="text-gray-400 text-sm">
                  {getContactContent('company_name', 'WeDo HR Services UG')}<br />
                  {getContactContent('address_line1', 'Düsseldorf')}, {getContactContent('address_line2', 'Germany')}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-[#33AADD] flex-shrink-0" />
                <a href={`tel:${getContactContent('phone', '+4915906453419')}`} className="text-gray-400 hover:text-[#33AADD] text-sm">
                  {getContactContent('phone_display', '+49 1590 6453419')}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-[#33AADD] flex-shrink-0" />
                <a href={`mailto:${getContactContent('email', 'info@wedohr.net')}`} className="text-gray-400 hover:text-[#33AADD] text-sm">
                  {getContactContent('email', 'info@wedohr.net')}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              {getFooterContent('copyright', t('footer.copyright'))}
            </p>
            <div className="flex items-center gap-6">
              {legalLinks.map((link, index) => (
                <Link
                  key={index}
                  to={link.href}
                  className="text-gray-500 hover:text-[#33AADD] transition-colors text-sm"
                  data-testid={`footer-legal-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
